import { useCallback, useEffect, useRef, useState } from 'react';
import { Bounds, Camera, clampCamera, clampScale, fitScale, minScale, Point, Size, zoomAt } from './digitalMapGeometry';
import { observeElementResize } from './observeElementResize';

export type MapCameraOptions = { board: Size; initialBounds: Bounds; storageKey: string; topInset?: number | null; enabled?: boolean };
const validSize = (size: Size) => size.width > 0 && size.height > 0;
export function useMapCamera({ board, initialBounds, storageKey, topInset = 0, enabled = true }: MapCameraOptions) {
  const { width: boardWidth, height: boardHeight } = board;
  const { x: initialX, y: initialY, width: initialWidth, height: initialHeight } = initialBounds;
  const viewportRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const sizeRef = useRef<Size>({ width: 0, height: 0 });
  const [camera, setCamera] = useState(cameraRef.current);
  const [ready, setReady] = useState(false);
  const commandRef = useRef<(command: 'in' | 'out' | 'fit') => void>(() => {});
  const centerRef = useRef<(point: Point, preserveZoom?: boolean) => void>(() => {});
  const centerOn = useCallback((point: Point, preserveZoom = false) => centerRef.current(point, preserveZoom), []);
  const stopMotionRef = useRef<() => void>(() => {});
  const stopMotion = useCallback(() => stopMotionRef.current(), []);
  const insetRef = useRef(topInset);
  const measureRef = useRef<() => void>(() => {});
  useEffect(() => {
    insetRef.current = topInset;
    measureRef.current();
  }, [topInset]);

  useEffect(() => {
    if (!enabled) return;
    const board = { width: boardWidth, height: boardHeight };
    const viewport = viewportRef.current;
    if (!viewport) return;
    let animation = 0, saveTimer: ReturnType<typeof setTimeout> | undefined;
    let measured = false;
    let inset = 0;
    const pointers = new Map<number, Point>();
    const origins = new Map<number, Point>();
    let samples: { point: Point; time: number }[] = [];
    let gestureHadPinch = false;
    let dragging = false, suppressClick = false;
    const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const stop = () => { cancelAnimationFrame(animation); animation = 0; };
    stopMotionRef.current = stop;
    const save = () => {
      if (!measured) return;
      const current = cameraRef.current, size = sizeRef.current;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ version: 2,
          centerX: (size.width / 2 - current.x) / current.scale,
          centerY: (size.height / 2 - current.y) / current.scale,
          zoom: current.scale / minScale(size, board) }));
      } catch { /* Session storage may be disabled. Navigation still works. */ }
    };
    const apply = (next: Camera) => {
      const clamped = clampCamera(next, sizeRef.current, board, inset);
      cameraRef.current = clamped;
      setCamera(clamped);
      clearTimeout(saveTimer);
      saveTimer = setTimeout(save, 150);
    };
    // Rebuild offsets only after bounding scale, so clamping never shifts the intended center.
    const atCenter = (center: Point, requestedScale: number, size: Size): Camera => {
      const scale = clampScale(requestedScale, size, board);
      return { scale, x: size.width / 2 - center.x * scale, y: size.height / 2 - center.y * scale };
    };
    const measure = () => {
      if (insetRef.current === null) return;
      const bounds = viewport.getBoundingClientRect();
      const size = { width: bounds.width, height: bounds.height };
      if (!validSize(size)) return;
      stop();
      const oldSize = sizeRef.current, old = cameraRef.current;
      const oldInset = inset;
      inset = Math.max(0, Math.min(insetRef.current, size.height - 1));
      sizeRef.current = size;
      if (!measured) {
        const scale = minScale(size, board);
        let initial = { scale, x: -initialX * scale, y: inset - initialY * scale };
        try {
          const stored = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null');
          if ((stored?.version === 1 || stored?.version === 2) && [stored.centerX, stored.centerY, stored.zoom].every(value => typeof value === 'number' && Number.isFinite(value))
            && stored.zoom >= 1 && stored.centerX >= 0
            && stored.centerY >= -inset / minScale(size, board)) {
            const reference = stored.version === 1 ? fitScale(size, board) : minScale(size, board);
            initial = atCenter({ x: stored.centerX, y: stored.centerY }, stored.zoom * reference, size);
          }
        } catch { /* Ignore stale, malformed or unavailable storage. */ }
        measured = true;
        apply(initial);
        setReady(true);
      } else {
        const scale = old.scale / minScale(oldSize, board) * minScale(size, board);
        const next = atCenter({ x: (oldSize.width / 2 - old.x) / old.scale,
          y: (oldSize.height / 2 - old.y) / old.scale }, scale, size);
        // A view parked at the board's top follows the measured clearance.
        if ((inset > 0 || inset !== oldInset) && Math.abs(old.y - oldInset) < .5) next.y = inset;
        apply(next);
      }
    };
    const local = (event: { clientX: number; clientY: number }): Point => {
      const rect = viewport.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const wheel = (event: WheelEvent) => {
      if (!measured) return;
      event.preventDefault(); stop();
      const size = sizeRef.current, current = cameraRef.current;
      const deltaX = event.deltaX * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? size.width : 1);
      const deltaY = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? size.height : 1);
      if (event.ctrlKey) {
        apply(zoomAt(current, size, local(event), Math.exp(-Math.max(-500, Math.min(500, deltaY)) * .002), board, inset));
      } else {
        apply({ ...current, x: current.x - deltaX, y: current.y - deltaY });
      }
    };
    const down = (event: PointerEvent) => {
      if (!measured || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (!pointers.size) { suppressClick = !!animation; dragging = false; gestureHadPinch = false; }
      stop();
      pointers.set(event.pointerId, local(event));
      origins.set(event.pointerId, local(event));
      if (pointers.size > 1) {
        gestureHadPinch = true; suppressClick = true;
        pointers.forEach((_, pointerId) => viewport.setPointerCapture?.(pointerId));
        event.preventDefault();
      }
      samples = [{ point: local(event), time: event.timeStamp }];
    };
    const move = (event: PointerEvent) => {
      if (!pointers.has(event.pointerId)) return;
      const before = Array.from(pointers.values()), next = local(event), previous = pointers.get(event.pointerId)!;
      pointers.set(event.pointerId, next);
      const after = Array.from(pointers.values());
      if (pointers.size >= 2) {
        event.preventDefault();
        const midpoint = (points: Point[]) => ({ x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 });
        const distance = (points: Point[]) => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const a = midpoint(before), b = midpoint(after);
        const zoomed = zoomAt(cameraRef.current, sizeRef.current, a, distance(after) / Math.max(distance(before), 1), board, inset);
        apply({ ...zoomed, x: zoomed.x + b.x - a.x, y: zoomed.y + b.y - a.y });
        samples = [];
      } else {
        const origin = origins.get(event.pointerId)!;
        let anchor = previous;
        if (!dragging) {
          if (Math.hypot(next.x - origin.x, next.y - origin.y) < 10) return;
          dragging = true; suppressClick = true; anchor = origin;
          viewport.setPointerCapture?.(event.pointerId);
          viewport.dataset.dragging = 'true';
        }
        event.preventDefault();
        apply({ ...cameraRef.current, x: cameraRef.current.x + next.x - anchor.x, y: cameraRef.current.y + next.y - anchor.y });
        samples = [...samples.filter(s => event.timeStamp - s.time <= 100), { point: next, time: event.timeStamp }];
      }
    };
    const finish = (event: PointerEvent) => {
      // Touch capture transfers from the hit child to the viewport when dragging starts.
      if (event.type === 'lostpointercapture' && event.target !== viewport) return;
      if (!pointers.has(event.pointerId)) return;
      if (event.type !== 'pointerup') suppressClick = true;
      pointers.delete(event.pointerId);
      origins.delete(event.pointerId);
      if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      if (pointers.size) { pointers.forEach((point, id) => origins.set(id, point)); dragging = false; samples = []; return; }
      delete viewport.dataset.dragging;
      const first = samples[0], last = samples[samples.length - 1];
      if (event.type === 'pointerup' && dragging && !gestureHadPinch && !reducedMotion() && first && last && last.time > first.time
        && event.timeStamp - last.time < 80) {
        let vx = (last.point.x - first.point.x) / (last.time - first.time), vy = (last.point.y - first.point.y) / (last.time - first.time);
        let previousTime = performance.now();
        const coast = (time: number) => {
          const dt = Math.min(32, time - previousTime); previousTime = time;
          const old = cameraRef.current;
          apply({ ...old, x: old.x + vx * dt, y: old.y + vy * dt });
          const current = cameraRef.current;
          if (Math.abs(current.x - old.x) < .01) vx = 0;
          if (Math.abs(current.y - old.y) < .01) vy = 0;
          vx *= Math.exp(-dt / 220); vy *= Math.exp(-dt / 220);
          if (Math.hypot(vx, vy) > .02) animation = requestAnimationFrame(coast);
          else { animation = 0; save(); }
        };
        animation = requestAnimationFrame(coast);
      }
      samples = []; save();
    };
    const blockGestureClick = (event: MouseEvent) => {
      if (suppressClick && event.detail !== 0) { event.preventDefault(); event.stopImmediatePropagation(); }
    };
    const preventImageDrag = (event: DragEvent) => event.preventDefault();
    const command = (value: 'in' | 'out' | 'fit') => {
      if (!measured) return;
      stop();
      const size = sizeRef.current;
      apply(value === 'fit' ? atCenter({ x: board.width / 2,
        y: (size.height / 2 - cameraRef.current.y) / cameraRef.current.scale }, minScale(size, board), size)
        : zoomAt(cameraRef.current, size, { x: size.width / 2, y: size.height / 2 }, value === 'in' ? 1.25 : .8, board, inset));
    };
    centerRef.current = (point, preserveZoom) => {
      if (!measured) return;
      stop();
      const next = atCenter(point, preserveZoom ? cameraRef.current.scale : Math.max(1, cameraRef.current.scale), sizeRef.current);
      next.y += inset / 2;
      apply(next);
      save();
    };
    commandRef.current = command;
    const keydown = (event: KeyboardEvent) => {
      if (event.target !== viewport || !measured) return;
      const directions: Record<string, Point> = { ArrowLeft: { x: 60, y: 0 }, ArrowRight: { x: -60, y: 0 }, ArrowUp: { x: 0, y: 60 }, ArrowDown: { x: 0, y: -60 } };
      if (directions[event.key]) {
        event.preventDefault(); stop(); const p = directions[event.key];
        apply({ ...cameraRef.current, x: cameraRef.current.x + p.x, y: cameraRef.current.y + p.y });
      } else if (['+', '=', '-', '0', 'Home'].includes(event.key)) {
        event.preventDefault(); command(event.key === '-' ? 'out' : ['0', 'Home'].includes(event.key) ? 'fit' : 'in');
      }
    };
    measureRef.current = measure;
    measure();
    const disconnect = observeElementResize(viewport, measure);
    window.addEventListener('resize', measure);
    viewport.addEventListener('wheel', wheel, { passive: false });
    viewport.addEventListener('pointerdown', down);
    viewport.addEventListener('pointermove', move);
    viewport.addEventListener('pointerup', finish);
    viewport.addEventListener('pointercancel', finish);
    viewport.addEventListener('lostpointercapture', finish);
    viewport.addEventListener('keydown', keydown);
    viewport.addEventListener('click', blockGestureClick, true);
    viewport.addEventListener('dragstart', preventImageDrag);
    return () => {
      stop(); clearTimeout(saveTimer); save(); disconnect();
      window.removeEventListener('resize', measure);
      viewport.removeEventListener('wheel', wheel);
      viewport.removeEventListener('pointerdown', down);
      viewport.removeEventListener('pointermove', move);
      viewport.removeEventListener('pointerup', finish);
      viewport.removeEventListener('pointercancel', finish);
      viewport.removeEventListener('lostpointercapture', finish);
      viewport.removeEventListener('keydown', keydown);
      viewport.removeEventListener('click', blockGestureClick, true);
      viewport.removeEventListener('dragstart', preventImageDrag);
      commandRef.current = () => {};
      centerRef.current = () => {};
      measureRef.current = () => {};
      stopMotionRef.current = () => {};
    };
  }, [enabled, storageKey, boardWidth, boardHeight, initialX, initialY, initialWidth, initialHeight]);
  return { viewportRef, camera, ready, stopMotion, centerOn, command: (value: 'in' | 'out' | 'fit') => commandRef.current(value) };
}
